import { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Plus, Search, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';
import { tokenManager } from '@/shared/utils/tokenManager';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Skeleton } from '@/components/ui/skeleton';
import { SidebarGroup } from '@/components/ui/sidebar';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import ConversationGroup from './ConversationGroup';

const API_BASE = import.meta.env.VITE_API_URL || 'http://localhost:8005';

function groupByDate(conversations, t) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const weekAgo = new Date(today);
  weekAgo.setDate(weekAgo.getDate() - 7);

  const groups = {
    today: { label: t('today', 'Today'), items: [] },
    yesterday: { label: t('yesterday', 'Yesterday'), items: [] },
    week: { label: t('previous_7_days', 'Previous 7 Days'), items: [] },
    older: { label: t('older', 'Older'), items: [] },
  };

  for (const conv of conversations) {
    const date = new Date(conv.updated_at || conv.created_at);
    if (date >= today) {
      groups.today.items.push(conv);
    } else if (date >= yesterday) {
      groups.yesterday.items.push(conv);
    } else if (date >= weekAgo) {
      groups.week.items.push(conv);
    } else {
      groups.older.items.push(conv);
    }
  }

  return groups;
}

export default function ConversationList() {
  const { t } = useTranslation('chat');
  const navigate = useNavigate();
  const { threadId: activeThreadId } = useParams();
  const [conversations, setConversations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);

  const fetchConversations = useCallback(async () => {
    const token = tokenManager.getAipToken();
    if (!token) return;

    try {
      const res = await fetch(`${API_BASE}/embed/v1/conversations/`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setConversations(data.conversations || []);
      }
    } catch {
      // Silently fail for sidebar
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 10000);

    const handleVisibility = () => {
      if (document.visibilityState === 'visible') {
        fetchConversations();
      }
    };
    document.addEventListener('visibilitychange', handleVisibility);

    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [fetchConversations]);

  const handleDelete = useCallback(
    async (threadId) => {
      const token = tokenManager.getAipToken();
      if (!token) return;

      try {
        const res = await fetch(`${API_BASE}/embed/v1/conversations/${threadId}`, {
          method: 'DELETE',
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          setConversations((prev) => prev.filter((c) => c.thread_id !== threadId));
          if (activeThreadId === threadId) {
            navigate('/');
          }
          toast.success(t('delete_success', 'Conversation deleted'));
        } else {
          toast.error(t('delete_error', 'Failed to delete conversation'));
        }
      } catch {
        toast.error(t('delete_error', 'Failed to delete conversation'));
      }
      setDeleteTarget(null);
    },
    [activeThreadId, navigate, t]
  );

  const filtered = useMemo(() => {
    if (!search.trim()) return conversations;
    const q = search.toLowerCase();
    return conversations.filter((c) => (c.title || '').toLowerCase().includes(q));
  }, [conversations, search]);

  const groups = useMemo(() => groupByDate(filtered, t), [filtered, t]);

  return (
    <>
      <SidebarGroup className="gap-2">
        {/* New chat + refresh */}
        <div className="flex gap-1.5">
          <Button
            variant="outline"
            size="sm"
            className="flex-1 justify-start gap-2"
            onClick={() => navigate('/')}
          >
            <Plus className="size-4" />
            {t('new_chat', 'New chat')}
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="size-8 shrink-0"
            onClick={fetchConversations}
          >
            <RefreshCw className="size-3.5" />
          </Button>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 size-3.5 text-muted-foreground" />
          <Input
            placeholder={t('search_conversations', 'Search...')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="h-8 pl-8 text-xs"
          />
        </div>
      </SidebarGroup>

      <ScrollArea className="flex-1 px-0">
        {isLoading ? (
          <div className="space-y-2 px-4 pt-2">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-2">
                <Skeleton className="size-4 rounded" />
                <Skeleton className="h-4 flex-1" />
              </div>
            ))}
          </div>
        ) : filtered.length === 0 ? (
          <div className="px-4 pt-4 text-xs text-muted-foreground text-center">
            {search ? t('no_results', 'No conversations found') : t('no_data', 'No conversations yet')}
          </div>
        ) : (
          Object.values(groups).map(
            (group) =>
              group.items.length > 0 && (
                <ConversationGroup
                  key={group.label}
                  label={group.label}
                  conversations={group.items}
                  onDelete={(threadId) => setDeleteTarget(threadId)}
                />
              )
          )
        )}
      </ScrollArea>

      {/* Delete confirmation dialog */}
      <Dialog open={!!deleteTarget} onOpenChange={() => setDeleteTarget(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('delete_confirm_title', 'Delete conversation?')}</DialogTitle>
            <DialogDescription>
              {t('delete_confirm_message', 'This action cannot be undone. The conversation will be permanently deleted.')}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)}>
              {t('cancel', 'Cancel')}
            </Button>
            <Button variant="destructive" onClick={() => handleDelete(deleteTarget)}>
              {t('delete', 'Delete')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
